import React, { useEffect, useState } from 'react';
import { Button, Checkbox, ClearableInput, Notification, Spinner } from '@rio-cloud/rio-uikit';
import { fieldLabelsSubject, fieldOptionsSubject } from '../../../../../support/constants';
import { createGroupTopic, createSubjectSupport, editSubjectSupport } from '../../../../../../api/tas';
import { Field, IResponseSubjectData } from '../List';
import { useIntl } from 'react-intl';

interface CreateSujectForm {
    onSuccess: () => void;
    onCancel: () => void;
    data?: IResponseSubjectData | null;
}

interface SubjectForm {
    code: string,
    disabled: boolean;
    fields: Field[];
    name: string,
}

const SubjectForm: React.FC<CreateSujectForm> = ({
    onSuccess,
    onCancel,
    data,
}) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const hasEdit = !!data?.id;
    const [formData, setFormData] = useState<SubjectForm>({
        code: '',
        disabled: false,
        fields: [],
        name: '',
    });

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name) {
            newErrors.name = 'Obrigatório';
        }

        if (!formData.code) {
            newErrors.code = 'Obrigatório';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);



        try {
            if (data?.id) {
                await editSubjectSupport(data?.id, formData);
            } else {
                await createSubjectSupport(formData);
            }
            onSuccess();
            Notification.success(
                hasEdit
                    ? intl.formatMessage({ id: 'intl-msg:tas.subject.success-edit' })
                    : intl.formatMessage({ id: 'intl-msg:tas.subject.success-create' })
            );

        } catch (error) {

            Notification.error(
                hasEdit
                    ? intl.formatMessage({ id: 'intl-msg:tas.subject.error-edit' })
                    : intl.formatMessage({ id: 'intl-msg:tas.subject.error-create' })
            );
        } finally {
            setLoading(false);
        }

    };

    const handleInputChange = (field: keyof SubjectForm, value: string | string[]) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const handleCheckedField = (field: string, value: boolean) => {
        setFormData(prev => ({
            ...prev,
            fields: prev.fields.map(item =>
                item.field === field
                    ? { ...item, required: value }
                    : item
            )
        }));
    };

    useEffect(() => {
        if (data) {
            setFormData({
                code: data.code,
                disabled: data.disabled,
                fields: data.fields,
                name: data.name,
            })
        } else {
            setFormData(prev => ({
                ...prev,
                fields: fieldOptionsSubject,
            }))
        }
    }, [data])

    const intl = useIntl();

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="name" className="block font-medium text-gray-700 mb-2">
                        {intl.formatMessage({ id: "intl-msg:backoffice-support-table-name" })} *
                    </label>
                    <ClearableInput id='name' value={formData.name} onChange={(v) => handleInputChange('name', v)} />
                    {errors.name && (
                        <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="code" className="block font-medium text-gray-700 mb-2">
                        {intl.formatMessage({ id: "intl-msg:backoffice-support-table-code" })} *
                    </label>
                    <ClearableInput id='code' value={formData.code} disabled={!!data?.code} onChange={(v) => handleInputChange('code', v)} />

                    {errors.code && (
                        <p className="text-red-600 text-sm mt-1">{errors.code}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="code" className="block font-medium text-gray-700 mb-2">
                        {intl.formatMessage({ id: "intl-msg:backoffice-support-table-fields" })}
                    </label>

                    <div className='flex flex-column gap-10'>
                        {formData?.fields?.map((field, idx) => {
                            return (
                                <div className='flex gap-10 align-items-center' key={idx?.toString()}>
                                    <input id="code" className="form-control" type="text" value={fieldLabelsSubject[field.field as keyof typeof fieldLabelsSubject]} disabled={true}></input>
                                    <Checkbox onChange={(event) => handleCheckedField(field.field, event.target.checked)} checked={field.required} inline />
                                </div>
                            )
                        })}
                    </div>

                    {errors.fields && (
                        <p className="text-red-600 text-sm mt-1">{errors.fields}</p>
                    )}
                </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6">
                <Button
                    bsStyle={Button.DEFAULT}
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button
                    bsStyle={Button.PRIMARY}
                    type="submit"
                    disabled={loading}
                    iconName={loading ? undefined : "rioglyph-check"}
                    onClick={handleSubmit}
                >
                    {loading ? (
                        <div className="flex items-center">
                            <Spinner />
                        </div>
                    ) : (
                        'Adicionar'
                    )}
                </Button>
            </div>
        </form>
    );
};

export default SubjectForm;

